<template>
  <Report
    :report-id="reportId"
    title="Proactive Triggers"
    tooltip-text="A trend showing the number of proactive flows that were started and the number that were blocked from starting due to the monthly limit being reached."
    class="chart-container"
    :data-empty="isDataEmpty"
    :loaded-data="isDataLoaded"
    :loading-error="loadingError"
  >
    <template #chart>
      <BarChart :reportId="reportId" class="chart" :data="reportData" :seriesOrder="seriesOrder" :stacked="true" />
    </template>
  </Report>
</template>

<script lang="ts" setup>
import BarChart from '@/features/reports/components/chartjs/BarChart.vue';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import ChartError from '@/features/reports/components/ChartError.vue';
import ChartLabels from '@/features/reports/components/ChartLabels.vue';
import ChartNoData from '@/features/reports/components/ChartNoData.vue';
import HelpIcon from 'vue-material-design-icons/HelpCircleOutline.vue';
import { Data, SeriesConfig } from '@/features/reports/models';
import { computed, PropType } from 'vue';
import { useReportData } from '@/features/reports/util/useReportData';
import Report from '@/features/reports/components/Report.vue';

const props = defineProps({
  data: {
    type: Object as PropType<Data>,
    required: true,
  },
  loadedData: {
    type: Boolean,
    default: false,
  },
  loadingError: {
    type: Boolean,
    default: false,
  },
});

const { reportId, isDataEmpty, isDataLoaded, createBlankSeries } = useReportData(props);

const seriesOrder: SeriesConfig[] = [
  { name: 'Started', displayName: 'Proactive Triggers Started', colour: '#062B80' },
  { name: 'Blocked', displayName: 'Proactive Triggers Blocked', colour: '#FF6666' },
];

const reportData = computed<Data>(() => ({
  scale: props.data.scale,
  series: seriesOrder.map((seriesConfig) => {
    return (
      props.data.series.find((s) => s.name === seriesConfig.name) ||
      createBlankSeries(seriesConfig.name, seriesConfig.displayName)
    );
  }),
}));
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@use '@/features/reports/components/reports.scss' as *;
</style>
